"use client"

import { useState, useRef, useEffect } from "react"
import { Filter, Download, User, LogOut, CalendarIcon, Menu } from "lucide-react"
import { Button } from "./button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./dropdown-menu"
import { PulseLogo } from "./pulse-logo"
import { NotificationDropdown } from "./notification-dropdown"

interface CriticalAlertsHeaderProps {
  onLogout: () => void
  onFiltersChange?: (filters: any) => void
  onExport?: () => void
  onSidebarToggle?: () => void
  title?: string
  failureTypeOptions?: Array<{ value: string; label: string }>
  theme?: 'gateway' | 'critical'
  externalStatus?: string
}

const THEMES = {
  gateway: {
    containerBg: "linear-gradient(135deg, #1a0b0b 0%, #4b0f12 100%)",
    containerShadow: "0 0 0 1px rgba(255,80,80,0.15), 0 6px 30px rgba(255,60,60,0.15)",
    inputBg: "rgba(120,20,20,0.25)",
    inputBorder: "1px solid rgba(255,120,120,0.25)",
    inputText: "#ffdede",
    labelColor: "#ffb3b3",
    iconColor: "#ff8a8a",
    dropdownBg: "#2a0f0f",
    dropdownBorder: "rgba(255,120,120,0.25)",
    clearBorder: "1px solid #ff6b6b",
    clearText: "#ffb3b3",
    clearBg: "transparent",
    clearHoverBg: "rgba(255,80,80,0.15)",
  },
  critical: {
    containerBg: "linear-gradient(135deg, #240909 0%, #5a0c0f 100%)",
    containerShadow: "0 0 0 1px rgba(255,70,70,0.2), 0 8px 32px rgba(255,50,50,0.18)",
    inputBg: "rgba(150,30,30,0.28)",
    inputBorder: "1px solid rgba(255,120,120,0.35)",
    inputText: "#ffe3e3",
    labelColor: "#ffc2c2",
    iconColor: "#ff9c9c",
    dropdownBg: "#350a0a",
    dropdownBorder: "rgba(255,120,120,0.35)",
    clearBorder: "1px solid #ff5a5a",
    clearText: "#ffc2c2",
    clearBg: "transparent",
    clearHoverBg: "rgba(255,70,70,0.18)",
  },
}

export function CriticalAlertsHeader({
  onLogout,
  onFiltersChange,
  onExport,
  onSidebarToggle,
  title = "Critical Alerts",
  failureTypeOptions,
  theme = 'critical',
  externalStatus,
}: CriticalAlertsHeaderProps) {
  const t = THEMES[theme]
  const [selectedTimeRange, setSelectedTimeRange] = useState("")
  const [filters, setFilters] = useState({
    failureType: "all",
    severity: "all",
    status: "all",
    startTime: "",
    endTime: ""
  })

  useEffect(() => {
    if (externalStatus === undefined) return
    setFilters((prev) =>
      prev.status === externalStatus ? prev : { ...prev, status: externalStatus }
    )
  }, [externalStatus])

  const [clearHovered, setClearHovered] = useState(false)
  const startTimeRef = useRef<HTMLInputElement>(null)
  const endTimeRef = useRef<HTMLInputElement>(null)

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    try {
      // Explicitly show picker on click for better UX since the input is transparent
      if ('showPicker' in e.currentTarget) {
        (e.currentTarget as any).showPicker();
      }
    } catch (error) {
      // Ignore errors (e.g. if browser blocks it or not supported)
    }
  }

  useEffect(() => {
    const allowedFailureTypes = new Set([
      "all",
      ...(failureTypeOptions ?? [
        { value: "service_unreachable", label: "Service Unreachable" },
        { value: "qure_services_unreachable", label: "Qure Services Unreachable" },
        { value: "high_unprocessed_images", label: "High Unprocessed Images" },
        { value: "pipeline_failure", label: "Pipeline Failure" },
      ]).map((option) => option.value),
    ])

    if (!allowedFailureTypes.has(filters.failureType)) {
      const newFilters = { ...filters, failureType: "all" }
      setFilters(newFilters)
      onFiltersChange?.(newFilters)
    }
  }, [failureTypeOptions, filters, onFiltersChange])

  const updateFilters = (key: string, value: string) => {
    const newFilters = { ...filters, [key]: value }
    setFilters(newFilters)
    onFiltersChange?.(newFilters)
  }

  // Generate current date/time defaults
  const now = new Date()
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  
  const formatDateTime = (date: Date) => {
    return date.toISOString().slice(0, 16) // Format for datetime-local input
  }

  const formatDisplayDateTime = (date: Date) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]
    
    const day = date.getDate()
    const month = months[date.getMonth()]
    const year = date.getFullYear()
    let hours = date.getHours()
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const ampm = hours >= 12 ? 'PM' : 'AM'
    
    hours = hours % 12
    hours = hours ? hours : 12 // 0 should be 12
    
    return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`
  }

  // Quick preset functions
  const setTimePreset = (preset: string) => {
    setSelectedTimeRange(preset)
    const now = new Date()
    let startTime: Date
    let endTime = now
    
    switch (preset) {
      case 'today':
        startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'yesterday':
        startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
        endTime = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'week':
        startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'month':
        startTime = new Date(now.getFullYear(), now.getMonth(), 1)
        break
      default:
        return
    }
    
    const newFilters = {
      ...filters,
      startTime: formatDateTime(startTime),
      endTime: formatDateTime(endTime)
    }
    setFilters(newFilters)
    onFiltersChange?.(newFilters)
  }

  const failureOptions = failureTypeOptions ?? [
    { value: "service_unreachable", label: "Service Unreachable" },
    { value: "qure_services_unreachable", label: "Qure Services Unreachable" },
    { value: "high_unprocessed_images", label: "High Unprocessed Images" },
    { value: "pipeline_failure", label: "Pipeline Failure" },
  ]

  return (
    <div style={{ backgroundColor: "#0d1117", boxShadow: "0 2px 10px rgba(0,0,0,0.35)" }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between px-6" style={{ height: "64px" }}>
        {/* Left: menu + brand breadcrumb */}
        <div className="flex items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onSidebarToggle}
            className="border-0 h-9 w-9 p-0 rounded-lg transition-colors hover:bg-white/5"
            style={{ backgroundColor: "rgba(255,255,255,0.04)", color: "#94a3b8" }}
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex items-center" style={{ marginLeft: "16px" }}>
            <PulseLogo size="sm" showText={false} />
            <span
              className="text-base font-semibold"
              style={{
                marginLeft: "8px",
                background: "linear-gradient(135deg, #ff8697 0%, #ff4d73 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              PULSE
            </span>
            <span
              className="text-sm select-none"
              style={{ marginLeft: "8px", marginRight: "8px", color: "rgba(255,255,255,0.35)" }}
            >
              /
            </span>
            <span className="text-sm font-medium" style={{ color: "#c9d1d9" }}>
              {title}
            </span>
          </div>
        </div>

        {/* Right: notification + optional export + user */}
        <div className="flex items-center" style={{ gap: "12px" }}>
          <NotificationDropdown
            style={{ backgroundColor: "rgba(255,255,255,0.04)", color: "#94a3b8" }}
          />

          {onExport && (
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              className="border-0 h-9 px-4 rounded-lg transition-colors hover:bg-white/5"
              style={{ backgroundColor: "rgba(255,255,255,0.04)", color: "#94a3b8" }}
            >
              <Download className="h-4 w-4 mr-2" />
              <span className="text-sm">Export CSV</span>
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-0 h-9 w-9 p-0 rounded-lg transition-colors hover:bg-white/5"
                style={{ backgroundColor: "rgba(255,255,255,0.04)", color: "#94a3b8" }}
              >
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" style={{ backgroundColor: "#1a1a1a", borderColor: "#2a2a2a" }}>
              <DropdownMenuItem onClick={onLogout} style={{ color: "#94a3b8" }}>
                <LogOut className="h-4 w-4 mr-2" />
                <span className="text-base">Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="px-6 pb-5">
        <div
          className="rounded-2xl px-5 py-4"
          style={{
            background: t.containerBg,
            boxShadow: t.containerShadow,
          }}
        >
          <div className="flex items-end gap-4 flex-wrap">

            {/* Failure Type Filter */}
            <div className="flex flex-col gap-1.5 flex-shrink-0">
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: t.labelColor }}>
                Failure Type
              </label>
              <Select value={filters.failureType} onValueChange={(value) => updateFilters('failureType', value)}>
                <SelectTrigger
                  className="w-52 h-9 text-sm rounded-xl focus:ring-1 transition-all"
                  style={{ backgroundColor: t.inputBg, border: t.inputBorder, color: t.inputText }}
                >
                  <SelectValue placeholder="All Failure Types" />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: t.dropdownBg, borderColor: t.dropdownBorder }}>
                  <SelectItem value="all" style={{ color: t.inputText }}>All Failure Types</SelectItem>
                  {failureOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} style={{ color: t.inputText }}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Severity Filter */}
            <div className="flex flex-col gap-1.5 flex-shrink-0">
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: t.labelColor }}>
                Severity
              </label>
              <Select value={filters.severity} onValueChange={(value) => updateFilters('severity', value)}>
                <SelectTrigger
                  className="w-40 h-9 text-sm rounded-xl focus:ring-1 transition-all"
                  style={{ backgroundColor: t.inputBg, border: t.inputBorder, color: t.inputText }}
                >
                  <SelectValue placeholder="All Severities" />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: t.dropdownBg, borderColor: t.dropdownBorder }}>
                  <SelectItem value="all" style={{ color: t.inputText }}>All Severities</SelectItem>
                  <SelectItem value="critical" style={{ color: t.inputText }}>Critical</SelectItem>
                  <SelectItem value="high" style={{ color: t.inputText }}>High</SelectItem>
                  <SelectItem value="medium" style={{ color: t.inputText }}>Medium</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="flex flex-col gap-1.5 flex-shrink-0">
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: t.labelColor }}>
                Status
              </label>
              <Select value={filters.status} onValueChange={(value) => updateFilters('status', value)}>
                <SelectTrigger
                  className="w-40 h-9 text-sm rounded-xl focus:ring-1 transition-all"
                  style={{ backgroundColor: t.inputBg, border: t.inputBorder, color: t.inputText }}
                >
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: t.dropdownBg, borderColor: t.dropdownBorder }}>
                  <SelectItem value="all" style={{ color: t.inputText }}>All Statuses</SelectItem>
                  <SelectItem value="active" style={{ color: t.inputText }}>Active</SelectItem>
                  <SelectItem value="acknowledged" style={{ color: t.inputText }}>Acknowledged</SelectItem>
                  <SelectItem value="resolved" style={{ color: t.inputText }}>Resolved</SelectItem>
                  <SelectItem value="resolved_today" style={{ color: t.inputText }}>Resolved Today</SelectItem>
                  <SelectItem value="archived" style={{ color: t.inputText }}>Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Time Range */}
            <div className="flex flex-col gap-1.5 flex-shrink-0">
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: t.labelColor }}>
                Time Range
              </label>
              <Select value={selectedTimeRange} onValueChange={(value) => setTimePreset(value)}>
                <SelectTrigger
                  className="w-40 h-9 text-sm rounded-xl focus:ring-1 transition-all"
                  style={{ backgroundColor: t.inputBg, border: t.inputBorder, color: t.inputText }}
                >
                  <SelectValue placeholder="Select Range" />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: t.dropdownBg, borderColor: t.dropdownBorder }}>
                  <SelectItem value="today" style={{ color: t.inputText }}>Today</SelectItem>
                  <SelectItem value="yesterday" style={{ color: t.inputText }}>Yesterday</SelectItem>
                  <SelectItem value="week" style={{ color: t.inputText }}>Last 7 Days</SelectItem>
                  <SelectItem value="month" style={{ color: t.inputText }}>Last 30 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Start Time */}
            <div className="flex flex-col gap-1.5 flex-shrink-0">
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: t.labelColor }}>
                Start Time
              </label>
              <div className="relative" style={{ width: "13rem" }}>
                <input
                  ref={startTimeRef}
                  type="datetime-local"
                  value={filters.startTime || formatDateTime(oneWeekAgo)}
                  onChange={(e) => updateFilters('startTime', e.target.value)}
                  onClick={handleInputClick}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  style={{ colorScheme: "dark" }}
                />
                <div
                  className="h-9 px-3 rounded-xl text-sm flex items-center cursor-pointer transition-all"
                  style={{ backgroundColor: t.inputBg, border: t.inputBorder, color: t.inputText }}
                >
                  <CalendarIcon className="h-3.5 w-3.5 mr-2 flex-shrink-0" style={{ color: t.iconColor }} />
                  <span className="truncate">
                    {filters.startTime ? formatDisplayDateTime(new Date(filters.startTime)) : formatDisplayDateTime(oneWeekAgo)}
                  </span>
                </div>
              </div>
            </div>

            {/* End Time */}
            <div className="flex flex-col gap-1.5 flex-shrink-0">
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: t.labelColor }}>
                End Time
              </label>
              <div className="relative" style={{ width: "13rem" }}>
                <input
                  ref={endTimeRef}
                  type="datetime-local"
                  value={filters.endTime || formatDateTime(now)}
                  onChange={(e) => updateFilters('endTime', e.target.value)}
                  onClick={handleInputClick}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  style={{ colorScheme: "dark" }}
                />
                <div
                  className="h-9 px-3 rounded-xl text-sm flex items-center cursor-pointer transition-all"
                  style={{ backgroundColor: t.inputBg, border: t.inputBorder, color: t.inputText }}
                >
                  <CalendarIcon className="h-3.5 w-3.5 mr-2 flex-shrink-0" style={{ color: t.iconColor }} />
                  <span className="truncate">
                    {filters.endTime ? formatDisplayDateTime(new Date(filters.endTime)) : formatDisplayDateTime(now)}
                  </span>
                </div>
              </div>
            </div>

            {/* Clear Filters */}
            <div className="flex flex-col gap-1.5 flex-shrink-0">
              <label className="text-xs font-semibold uppercase tracking-wider invisible">Clear</label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFilters({ failureType: "all", severity: "all", status: "all", startTime: "", endTime: "" });
                  setSelectedTimeRange("");
                  onFiltersChange?.({ failureType: "all", severity: "all", status: "all", startTime: "", endTime: "" });
                }}
                onMouseEnter={() => setClearHovered(true)}
                onMouseLeave={() => setClearHovered(false)}
                className="h-9 px-4 text-sm font-medium rounded-xl transition-all"
                style={{
                  backgroundColor: clearHovered ? t.clearHoverBg : t.clearBg,
                  border: t.clearBorder,
                  color: t.clearText,
                }}
              >
                <Filter className="h-3.5 w-3.5 mr-1.5" />
                Clear Filters
              </Button>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
} 